import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { ApiService } from '../../../../core/services/api.service';
import { NotificationService } from '../../../../core/services/notification.service';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { Task, TaskComment, Sprint } from '../../../../models/task.model';
import { TaskFormDialogComponent } from '../task-form-dialog/task-form-dialog.component';

export interface TaskDetailData {
  taskId: number;
  projectId: number;
  isProjectAdmin?: boolean;
  isProjectMember?: boolean;
  sprints?: Sprint[];
  currentUserId?: number;
  focusComments?: boolean;
}

@Component({
  selector: 'app-task-detail-dialog',
  standalone: true,
  imports: [
    DatePipe,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatDividerModule,
  ],
  templateUrl: './task-detail-dialog.component.html',
  styleUrl: './task-detail-dialog.component.scss',
})
export class TaskDetailDialogComponent implements OnInit {
  readonly data = inject<TaskDetailData>(MAT_DIALOG_DATA);
  private readonly api    = inject(ApiService);
  private readonly dialog = inject(MatDialog);
  private readonly notify = inject(NotificationService);
  readonly ref = inject(MatDialogRef<TaskDetailDialogComponent>);

  @ViewChild('commentsSection') commentsSection?: ElementRef<HTMLElement>;

  task: Task | null = null;
  loading = true;
  dirty = false;

  commentCtrl  = new FormControl('', [Validators.required, Validators.minLength(1)]);
  editCommentId: number | null = null;
  editCommentCtrl = new FormControl('');
  savingComment = false;

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading = true;
    this.api.get<Task>(`/api/projects/${this.data.projectId}/tasks/${this.data.taskId}`).subscribe({
      next:  (t) => {
        this.task = t;
        this.loading = false;
        if (this.data.focusComments) {
          setTimeout(() => this.commentsSection?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
        }
      },
      error: () => { this.loading = false; },
    });
  }

  openEdit(): void {
    if (!this.task) return;
    this.dialog.open(TaskFormDialogComponent, {
      width: '600px',
      data: {
        projectId: this.data.projectId,
        task:      this.task,
        sprints:   this.data.sprints,
      },
    }).afterClosed().subscribe((result: Task | undefined) => {
      if (result) {
        this.dirty = true;
        this.load();
        this.notify.success('Task updated.');
      }
    });
  }

  // ── Comments ─────────────────────────────────────────────────────────────

  submitComment(): void {
    if (this.commentCtrl.invalid || this.savingComment) return;
    this.savingComment = true;
    this.api.post<TaskComment>(
      `/api/projects/${this.data.projectId}/tasks/${this.data.taskId}/comments`,
      { body: this.commentCtrl.value!.trim() },
    ).subscribe({
      next: () => {
        this.commentCtrl.reset();
        this.savingComment = false;
        this.dirty = true;
        this.load();
      },
      error: () => { this.savingComment = false; },
    });
  }

  startEditComment(comment: TaskComment): void {
    this.editCommentId = comment.id;
    this.editCommentCtrl.setValue(comment.body);
  }

  cancelEditComment(): void {
    this.editCommentId = null;
    this.editCommentCtrl.setValue('');
  }

  saveEditComment(comment: TaskComment): void {
    const body = this.editCommentCtrl.value?.trim();
    if (!body) return;
    this.api.patch<TaskComment>(
      `/api/projects/${this.data.projectId}/tasks/${this.data.taskId}/comments/${comment.id}`,
      { body },
    ).subscribe({
      next: () => {
        this.cancelEditComment();
        this.dirty = true;
        this.load();
      },
    });
  }

  deleteComment(comment: TaskComment): void {
    this.dialog.open(ConfirmDialogComponent, {
      width: '380px',
      data: { title: 'Delete comment?', message: 'This comment will be permanently removed.' },
    }).afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.api.delete(
        `/api/projects/${this.data.projectId}/tasks/${this.data.taskId}/comments/${comment.id}`,
      ).subscribe({
        next: () => { this.dirty = true; this.load(); },
      });
    });
  }

  canEditComment(comment: TaskComment): boolean {
    return comment.author.id === this.data.currentUserId || !!this.data.isProjectAdmin;
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  typeLabel(t: string): string {
    return { epic: 'Epic', story: 'Story', task: 'Task', bug: 'Bug', subtask: 'Subtask' }[t] ?? t;
  }

  typeIcon(t: string): string {
    return { epic: 'bolt', story: 'bookmark', task: 'check_box_outline_blank', bug: 'bug_report', subtask: 'subdirectory_arrow_right' }[t] ?? 'check_box_outline_blank';
  }

  statusIcon(s: string): string {
    return { to_do: 'radio_button_unchecked', in_progress: 'pending', in_review: 'rate_review', done: 'check_circle' }[s] ?? 'radio_button_unchecked';
  }

  priorityIcon(p: string): string {
    return { critical: 'emergency', high: 'keyboard_arrow_up', medium: 'remove', low: 'keyboard_arrow_down' }[p] ?? 'remove';
  }

  doneSubtasks(): number {
    return this.task?.subtasks?.filter(s => s.status === 'done').length ?? 0;
  }

  subtaskProgress(): number {
    const total = this.task?.subtasks?.length ?? 0;
    return total > 0 ? Math.round((this.doneSubtasks() / total) * 100) : 0;
  }

  statusLabel(s: string): string {
    return { to_do: 'To Do', in_progress: 'In Progress', in_review: 'In Testing', done: 'Done' }[s] ?? s;
  }

  priorityLabel(p: string): string {
    return { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' }[p] ?? p;
  }

  activityLabel(field: string, value: string | null): string {
    if (!value) return '—';
    if (field === 'status')   return this.statusLabel(value);
    if (field === 'priority') return this.priorityLabel(value);
    return value;
  }

  initial(name: string): string { return name ? name.charAt(0).toUpperCase() : '?'; }

  fmtDate(iso: string | null | undefined): string {
    if (!iso) return '—';
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  close(): void { this.ref.close(this.dirty); }
}
